import { useFormik } from "formik";
import InputField from "../../ui/form/InputField";
import { MedicalAidFamilyValidation } from "../../../validation/formValidation";
import usePost from "../../../hooks/api/usePost";
import apiRequest from "../../../utils/apiRequest";
import FileInputField from "../../ui/form/FileInputField";
import Button from "../../ui/Button";
import React, { useState } from "react";
import toast from "react-hot-toast";
import { formatDateForInput } from "../../../utils/helper";
import SelectField from "../../ui/form/SelectField";
import axios from "axios";
import handleApiError from "./handleApiError";
import FullScreenLoader from "../../ui/FullScreenLoader";
import PaymentOptions from "./PaymentOptions";
import { DASHBOARD_ROUTE, HOME_ROUTE } from "../../../routes/web/rootRoutes";
import { useNavigate } from "react-router-dom";

const MedicalAidFormFamilyMember = ({ data = {} }) => {
  const [errors, setErrors] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [submissionResponse, setSubmissionResponse] = useState(null);
  const [submittedFormData, setSubmittedFormData] = useState(null);
  const navigate = useNavigate(); 
  const userString = localStorage.getItem('user');
  let union_code;
  if (userString) {
    const user = JSON.parse(userString);
    union_code = user.union_code;
  }

  const {
    post,
    isLoading: postLoading,
    error,
    serverErrors,
  } = usePost(apiRequest.createForm);

  const formik = useFormik({
    validationSchema: MedicalAidFamilyValidation,

    initialValues: {
      // Member Details
      member_no: data?.member_no || union_code || "",
      date: data?.date || formatDateForInput(new Date()),
      member_name: data?.member_name || "",
      designation: data?.designation || "",
      married_unmarried: data?.married_unmarried || "",
      addhar_no: data?.addhar_no || "",
      address: data?.address || "",
      mobile: data?.mobile || "",
      email: data?.email || "",

      // Family Member Details
      family_member: data?.family_member || "",
      relation_family_member: data?.relation_family_member || "",

      // Medical Details
      hospital_address: data?.hospital_address || "",
      medical_aid: data?.medical_aid || "",
      medical_expense: data?.medical_expense || "",
      approved_medical_amount: data?.approved_medical_amount || "",

      // File Uploads
      photo: null,
      family_photo: null,
      member_signature: null,
      member_card: null,
      hospital_charge: null,
      subscription: null,
      medical_bill: null,
      addhar_card: null,
      hospital_discharge_card: null,
      member_hospital_charge: null,

      // Agreement
      agree: false,
    },

    onSubmit: async (values, { resetForm }) => {
      setIsSubmitting(true);
      
      try {
        const formData = new FormData();

        // Append all text fields
        const textFields = {
          member_no: values.member_no,
          date: values.date,
          member_name: values.member_name,
          designation: values.designation,
          address: values.address,
          addhar_no: values.addhar_no,
          mobile: values.mobile,
          email: values.email,
          family_member: values.family_member,
          relation_family_member: values.relation_family_member,
          hospital_address: values.hospital_address,
          medical_aid: values.medical_aid,
          medical_expense: values.medical_expense,
          approved_medical_amount: values.approved_medical_amount,
          married_unmarried: values.married_unmarried,
        };

        Object.entries(textFields).forEach(([key, value]) => {
          if (value) formData.append(key, value.toString());
        });

        // File mapping with correct backend field names
        const fileMap = {
          photo: "member_photo",
          family_photo: "member_family_photo",
          member_signature: "member_member_signature",
          member_card: "member_member_card",
          member_hospital_charge: "member_hospital_charge",
          subscription: "member_subscription",
          medical_bill: "member_medical_bill",
          addhar_card: "member_addhar_card",
          hospital_discharge_card: "member_hospital_discharge_card",
        };

        Object.entries(fileMap).forEach(([formKey, apiKey]) => {
          if (values[formKey]) {
            formData.append(apiKey, values[formKey]);
            console.log(`📎 Attached file: ${apiKey}`, values[formKey].name);
          }
        });

        // Debug log
        console.log("Submitting Medical Aid (Family) Form:");
        for (let [key, val] of formData.entries()) {
          console.log(`${key}:`, val instanceof File ? `File: ${val.name}` : val);
        }

        const token = localStorage.getItem("useAccessToken");
        if (!token) {
          toast.error("Authentication token not found. Please login again.");
          setIsSubmitting(false);
          return;
        }

        const apiUrl = process.env.REACT_APP_API_URL;
        if (!apiUrl) {
          toast.error("API URL not configured");
          setIsSubmitting(false);
          return;
        }

        const response = await axios.post(
          `${apiUrl}medical-aid-family`,
          formData,
          {
            headers: {
              Accept: "application/json",
              "Content-Type": "multipart/form-data",
              Authorization: token ? `Bearer ${token}` : "",
            },
            maxBodyLength: Infinity,
            timeout: 30000,
          }
        );

        console.log("✅ Medical Aid (Family) Form submitted:", response.data);
        
        setSubmissionResponse(response.data);
        setSubmittedFormData(values);
        
        toast.success("Medical Aid Application submitted successfully!");
        resetForm();


        setTimeout(() => {
              navigate(DASHBOARD_ROUTE);

          setIsSubmitting(false);
          setShowPayment(true);
        }, 1500);

      } catch (error) {
        console.error("Submission error:", error);
        
        if (error.response) {
          console.error("Server Error:", error.response.data);
          toast.error(`Server error: ${error.response.status} - ${error.response.data?.message || 'Unknown error'}`);
        } else if (error.request) {
          console.error("No response from server");
          toast.error("No response from server. Please check your internet connection.");
        } else {
          console.error("Request error:", error.message);
          toast.error(`Error: ${error.message}`);
        }
        
        handleApiError(error, setErrors, "Failed to submit Medical Aid Application form");
        setIsSubmitting(false);
      }
    },
  });

  const setFieldValue = (field, value) => {
    formik.setFieldValue(field, value);
  };

  const handleClosePayment = () => {
    setShowPayment(false);
    window.location.href = HOME_ROUTE;
  };

  if (showPayment) {
    return (
      <>
        {showPayment && (
          <PaymentOptions
            onClose={handleClosePayment}
          />
        )}
      </>
    );
  }

  return (
    <>
      {isSubmitting && (
        <FullScreenLoader message="Submitting medical aid application..." />
      )}

      <div className="max-w-7xl mx-auto p-6 bg-gradient-to-br min-h-screen">
        <div className="bg-white rounded-2xl shadow overflow-hidden">
          <div className="bg-gradient-to-r from-violet-500 to-violet-900 text-white p-6">
            <h1 className="text-center text-3xl font-bold mb-2">
              MEDICAL AID APPLICATION FOR FAMILY MEMBER
            </h1>
          </div>

          <form onSubmit={formik.handleSubmit} className="p-8">
            {/* Section 1: Member Number & Date */}
            <div className="mb-8 p-6 bg-gray-50 rounded-lg border-l-4 border-violet-500">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="block font-semibold text-gray-700">
                    Member No. <span className="text-red-500">*</span>
                  </label>
                  <InputField
                    id="member_no"
                    placeholder="Enter Membership No"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.member_no}
                    error={formik.touched.member_no && formik.errors.member_no}
                    serverError={serverErrors?.member_no}
                  />
                </div>
                <div className="space-y-2">
                  <label className="block font-semibold text-gray-700">
                    Date <span className="text-red-500">*</span>
                  </label>
                  <InputField
                    id="date"
                    type="date"
                    readOnly
                    placeholder="Select Date"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.date}
                    error={formik.touched.date && formik.errors.date}
                    serverError={serverErrors?.date}
                  />
                </div>
              </div>
            </div>

            {/* Section 2: Address Section */}
            <div className="mb-8 p-6 bg-blue-50 rounded-lg border-l-4 border-blue-500">
              <h3 className="text-xl font-semibold text-gray-800 mb-1">To,</h3>
              <h3 className="text-lg font-medium text-gray-700 mb-1">The President / General Secretary</h3>
              <h3 className="text-lg font-medium text-gray-700">Association of Cine & Television Art Directors & Costume Designers</h3>
            </div>

            {/* Section 3: Member Personal Details */}
            <div className="mb-8 p-6 bg-green-50 rounded-lg border-l-4 border-green-500">
              <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                <span className="mr-2">👤</span> Member Personal Details
              </h2>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="block font-semibold text-gray-700">
                    Member's Name <span className="text-red-500">*</span>
                  </label>
                  <InputField
                    id="member_name"
                    placeholder="Enter Member Name"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.member_name}
                    error={formik.touched.member_name && formik.errors.member_name}
                    serverError={serverErrors?.member_name}
                  />
                </div>

                <div className="space-y-2">
                  <label className="block font-semibold text-gray-700">
                    Designation <span className="text-red-500">*</span>
                  </label>
                  <InputField
                    id="designation"
                    placeholder="Enter designation"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.designation}
                    error={formik.touched.designation && formik.errors.designation}
                    serverError={serverErrors?.designation}
                  />
                </div>

                <div className="space-y-2">
                  <SelectField
                    options={[
                      { value: 'Married', label: 'Married' },
                      { value: 'UnMarried', label: 'UnMarried' },
                    ]}
                    label="Marital Status <span className='text-red-500'>*</span>"
                    id="married_unmarried"
                    value={formik.values.married_unmarried}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.married_unmarried && formik.errors.married_unmarried}
                    serverError={serverErrors?.married_unmarried}
                  />
                </div>

                <div className="space-y-2">
                  <label className="block font-semibold text-gray-700">
                    Aadhar No <span className="text-red-500">*</span>
                  </label>
                  <InputField
                    id="addhar_no"
                    placeholder="Enter Aadhar No"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.addhar_no}
                    error={formik.touched.addhar_no && formik.errors.addhar_no}
                    serverError={serverErrors?.addhar_no}
                  />
                </div>

                <div className="space-y-2 lg:col-span-2">
                  <label className="block font-semibold text-gray-700">
                    Residential Address <span className="text-red-500">*</span>
                  </label>
                  <InputField
                    id="address"
                    placeholder="Enter complete address"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.address}
                    error={formik.touched.address && formik.errors.address}
                    serverError={serverErrors?.address}
                  />
                </div>

                <div className="space-y-2">
                  <label className="block font-semibold text-gray-700">
                    Mobile No. <span className="text-red-500">*</span>
                  </label>
                  <InputField
                    id="mobile"
                    placeholder="Enter mobile number"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.mobile}
                    error={formik.touched.mobile && formik.errors.mobile}
                    serverError={serverErrors?.mobile}
                  />
                </div>

                <div className="space-y-2">
                  <label className="block font-semibold text-gray-700">
                    Email ID <span className="text-red-500">*</span>
                  </label>
                  <InputField
                    id="email"
                    type="email"
                    placeholder="Enter email address"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.email}
                    error={formik.touched.email && formik.errors.email}
                    serverError={serverErrors?.email}
                  />
                </div>
              </div>
            </div>

            {/* Section 4: Family Member Details */}
            <div className="mb-8 p-6 bg-yellow-50 rounded-lg border-l-4 border-yellow-500">
              <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                <span className="mr-2">👨‍👩‍👧‍👦</span> Family Member Details
              </h2>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="block font-semibold text-gray-700">
                    Name of Family Member <span className="text-red-500">*</span>
                  </label>
                  <InputField
                    id="family_member"
                    placeholder="Enter Name of Family Member"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.family_member}
                    error={formik.touched.family_member && formik.errors.family_member}
                    serverError={serverErrors?.family_member}
                  />
                </div>

                <div className="space-y-2">
                  <label className="block font-semibold text-gray-700">
                    Relation with Family Member <span className="text-red-500">*</span>
                  </label>
                  <InputField
                    id="relation_family_member"
                    placeholder="Enter Relation"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.relation_family_member}
                    error={formik.touched.relation_family_member && formik.errors.relation_family_member}
                    serverError={serverErrors?.relation_family_member}
                  />
                </div>
              </div>
            </div>

            {/* Section 5: Medical Details */}
            <div className="mb-8 p-6 bg-orange-50 rounded-lg border-l-4 border-orange-500">
              <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                <span className="mr-2">🏥</span> Medical Details
              </h2>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-2 lg:col-span-2">
                  <label className="block font-semibold text-gray-700">
                    Reason for Medical Aid <span className="text-red-500">*</span>
                  </label>
                  <InputField
                    id="medical_aid"
                    placeholder="Enter Reason for Medical Aid"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.medical_aid}
                    error={formik.touched.medical_aid && formik.errors.medical_aid}
                    serverError={serverErrors?.medical_aid}
                  />
                </div>

                <div className="space-y-2 lg:col-span-2">
                  <label className="block font-semibold text-gray-700">
                    Hospital Name & Address <span className="text-red-500">*</span>
                  </label>
                  <InputField
                    id="hospital_address"
                    placeholder="Enter Hospital Name & Address"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.hospital_address}
                    error={formik.touched.hospital_address && formik.errors.hospital_address}
                    serverError={serverErrors?.hospital_address}
                  />
                </div>

                <div className="space-y-2">
                  <label className="block font-semibold text-gray-700">
                    Total Medical Expense <span className="text-red-500">*</span>
                  </label>
                  <InputField
                    id="medical_expense"
                    type="number"
                    placeholder="Enter Total Medical Expense"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.medical_expense}
                    error={formik.touched.medical_expense && formik.errors.medical_expense}
                    serverError={serverErrors?.medical_expense}
                  />
                </div>

                <div className="space-y-2">
                  <label className="block font-semibold text-gray-700">
                    Approved Medical Aid Amount <span className="text-red-500">*</span>
                  </label>
                  <InputField
                    id="approved_medical_amount"
                    type="number"
                    placeholder="Enter Approved Amount"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.approved_medical_amount}
                    error={formik.touched.approved_medical_amount && formik.errors.approved_medical_amount}
                    serverError={serverErrors?.approved_medical_amount}
                  />
                </div>
              </div>
            </div>

            {/* Section 6: Documents Upload */}
            <div className="mb-8 p-6 bg-purple-50 rounded-lg border-l-4 border-purple-500">
              <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                <span className="mr-2">📎</span> Documents Upload
                <span className="text-sm font-normal text-gray-600 ml-2">(Please upload all required documents)</span>
              </h2>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Required Documents */}
                <div className="space-y-4 p-6 bg-red-50 rounded-lg border border-red-200">
                  <h3 className="text-lg font-semibold text-red-800 mb-4 flex items-center">
                    <span className="mr-2">📋</span> Required Documents
                  </h3>

                  <div className="space-y-4">
                    <div className="space-y-2">
                      <label className="block font-semibold text-gray-700">
                        Membership Card Copy <span className="text-red-500">*</span>
                      </label>
                      <FileInputField
                        id="member_card"
                        onChange={(event) => {
                          setFieldValue("member_card", event.currentTarget.files[0]);
                        }}
                        onBlur={formik.handleBlur}
                        error={formik.touched.member_card && formik.errors.member_card}
                        serverError={serverErrors?.member_card}
                      />
                      <p className="text-xs text-gray-500">Upload scanned copy of membership card</p>
                    </div>

                    <div className="space-y-2">
                      <label className="block font-semibold text-gray-700">
                        Aadhar Card Copy <span className="text-red-500">*</span>
                      </label>
                      <FileInputField
                        id="addhar_card"
                        onChange={(event) => {
                          setFieldValue("addhar_card", event.currentTarget.files[0]);
                        }}
                        onBlur={formik.handleBlur}
                        error={formik.touched.addhar_card && formik.errors.addhar_card}
                        serverError={serverErrors?.addhar_card}
                      />
                      <p className="text-xs text-gray-500">Upload scanned copy of Aadhar card</p>
                    </div>
                  </div>
                </div>

                {/* Medical Documents */}
                <div className="space-y-4 p-6 bg-green-50 rounded-lg border border-green-200">
                  <h3 className="text-lg font-semibold text-green-800 mb-4 flex items-center">
                    <span className="mr-2">💊</span> Medical Documents
                  </h3>

                  <div className="space-y-4">
                    <div className="space-y-2">
                      <label className="block font-semibold text-gray-700">
                        Original Medical Bills <span className="text-red-500">*</span>
                      </label>
                      <FileInputField
                        id="medical_bill"
                        onChange={(event) => {
                          setFieldValue("medical_bill", event.currentTarget.files[0]);
                        }}
                        onBlur={formik.handleBlur}
                        error={formik.touched.medical_bill && formik.errors.medical_bill}
                        serverError={serverErrors?.medical_bill}
                      />
                      <p className="text-xs text-gray-500">Normal Delivery/Mediclaim not allowed</p>
                    </div>

                    <div className="space-y-2">
                      <label className="block font-semibold text-gray-700">
                        Hospital Discharge Card <span className="text-red-500">*</span>
                      </label>
                      <FileInputField
                        id="hospital_discharge_card"
                        onChange={(event) => {
                          setFieldValue("hospital_discharge_card", event.currentTarget.files[0]);
                        }}
                        onBlur={formik.handleBlur}
                        error={formik.touched.hospital_discharge_card && formik.errors.hospital_discharge_card}
                        serverError={serverErrors?.hospital_discharge_card}
                      />
                      <p className="text-xs text-gray-500">Minimum 24 hours hospitalization mandatory</p>
                    </div>

                    <div className="space-y-2">
                      <label className="block font-semibold text-gray-700">
                        Member Hospital Charge
                      </label>
                      <FileInputField
                        id="member_hospital_charge"
                        onChange={(event) => {
                          setFieldValue("member_hospital_charge", event.currentTarget.files[0]);
                        }}
                        onBlur={formik.handleBlur}
                        error={formik.touched.member_hospital_charge && formik.errors.member_hospital_charge}
                        serverError={serverErrors?.member_hospital_charge}
                      />
                      <p className="text-xs text-gray-500">Upload hospital charge document</p>
                    </div>
                  </div>
                </div>

                {/* Financial Documents */}
                <div className="space-y-4 p-6 bg-blue-50 rounded-lg border border-blue-200">
                  <h3 className="text-lg font-semibold text-blue-800 mb-4 flex items-center">
                    <span className="mr-2">💰</span> Financial Documents
                  </h3>

                  <div className="space-y-4">
                    <div className="space-y-2">
                      <label className="block font-semibold text-gray-700">
                        Subscription Receipt <span className="text-red-500">*</span>
                      </label>
                      <FileInputField
                        id="subscription"
                        onChange={(event) => {
                          setFieldValue("subscription", event.currentTarget.files[0]);
                        }}
                        onBlur={formik.handleBlur}
                        error={formik.touched.subscription && formik.errors.subscription}
                        serverError={serverErrors?.subscription}
                      />
                      <p className="text-xs text-gray-500">Current year subscription paid receipt</p>
                    </div>
                  </div>
                </div>

                {/* Photos & Signatures - Full Width */}
                <div className="space-y-4 p-6 bg-pink-50 rounded-lg border border-pink-200 lg:col-span-2">
                  <h3 className="text-lg font-semibold text-pink-800 mb-4 flex items-center">
                    <span className="mr-2">📸</span> Photos & Signatures
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="space-y-2">
                      <label className="block font-semibold text-gray-700">
                        Member Photo <span className="text-red-500">*</span>
                      </label>
                      <FileInputField
                        id="photo"
                        onChange={(event) => {
                          setFieldValue("photo", event.currentTarget.files[0]);
                        }}
                        onBlur={formik.handleBlur}
                        error={formik.touched.photo && formik.errors.photo}
                        serverError={serverErrors?.photo}
                      />
                      <p className="text-xs text-gray-500">Passport size photo</p>
                    </div>

                    <div className="space-y-2">
                      <label className="block font-semibold text-gray-700">
                        Family Member Photo <span className="text-red-500">*</span>
                      </label>
                      <FileInputField
                        id="family_photo"
                        onChange={(event) => {
                          setFieldValue("family_photo", event.currentTarget.files[0]);
                        }}
                        onBlur={formik.handleBlur}
                        error={formik.touched.family_photo && formik.errors.family_photo}
                        serverError={serverErrors?.family_photo}
                      />
                      <p className="text-xs text-gray-500">Family member's photo</p>
                    </div>

                    <div className="space-y-2">
                      <label className="block font-semibold text-gray-700">
                        Member Signature <span className="text-red-500">*</span>
                      </label>
                      <FileInputField
                        id="member_signature"
                        onChange={(event) => {
                          setFieldValue("member_signature", event.currentTarget.files[0]);
                        }}
                        onBlur={formik.handleBlur}
                        error={formik.touched.member_signature && formik.errors.member_signature}
                        serverError={serverErrors?.member_signature}
                      />
                      <p className="text-xs text-gray-500">Upload scanned signature</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 7: Declaration */}
            <div className="mb-8 p-6 bg-indigo-50 rounded-lg border-l-4 border-indigo-500">
              <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                <span className="mr-2">📜</span> Declaration
              </h2>

              <div className="space-y-4">
                <p className="text-sm text-gray-700">
                  I hereby declare that the information provided above is true and correct to the best of my knowledge. 
                  I request for medical aid for my family member as per the association rules and regulations.
                </p>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="agree"
                    name="agree"
                    onChange={(e) => formik.setFieldValue('agree', e.target.checked)}
                    className="w-4 h-4 text-violet-600 border-gray-300 rounded focus:ring-violet-500"
                  />
                  <label htmlFor="agree" className="ml-2 text-sm text-gray-700">
                    I confirm that all information provided is correct and I have attached all required documents <span className="text-red-500">*</span>
                  </label>
                </div>
                {formik.touched.agree && formik.errors.agree && (
                  <p className="mt-1 text-xs text-red-500">{formik.errors.agree}</p>
                )}
              </div>
            </div>

            {/* Submit Button */}
            <div className="mt-8 flex justify-end items-center bg-gray-50 p-6 rounded-lg">
              <Button
                variant="primary"
                type="submit"
                isLoading={isSubmitting}
                disabled={isSubmitting}
                className="px-8 py-3 text-lg"
              >
                {isSubmitting ? 'Submitting...' : '📤 Submit Application'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default MedicalAidFormFamilyMember;